import dayjs from 'dayjs';
import config from '@config';
import { assembleBenefitListPayload } from '@/components/Conference/Organization/components/AddOrganization/utils/benefitTransform';
import { stringifyRealizationFiles } from '@/components/Conference/Organization/components/BenefitRealizationModal/realizationTransform';

const SITE_MODULE_SCALAR_KEYS = ['id', 'displayTitle', 'displayTitleEn', 'hiddenWeb', 'hiddenH5', 'status', 'path', 'moduleType'];

function stripOssFromUrl(url) {
    if (typeof url !== 'string' || !url) {
        return url;
    }
    const prefix = config.OSS_PREFIX;
    if (prefix && url.includes(prefix)) {
        const re = new RegExp(`${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(.*)`);
        const match = url.match(re);
        return match ? match[1] : url;
    }
    return url;
}

function normalizeUploadItem(item) {
    if (!item || typeof item !== 'object') {
        return item;
    }
    const next = { ...item };
    delete next.uid;
    if ('url' in next) {
        next.url = stripOssFromUrl(next.url);
    }
    return next;
}

/** 深拷贝并排序对象键，归一化上传项与 OSS 路径，便于 before/after 对比 */
function normalizeDeep(value) {
    if (Array.isArray(value)) {
        return value.map(entry => normalizeDeep(entry));
    }
    if (value && typeof value === 'object') {
        if ('url' in value && Object.keys(value).length <= 3) {
            return normalizeUploadItem(value);
        }
        const out = {};
        Object.keys(value)
            .sort()
            .forEach(key => {
                out[key] = normalizeDeep(value[key]);
            });
        return out;
    }
    return value;
}

function parseModuleDataField(data) {
    if (data == null || data === '') {
        return null;
    }
    if (typeof data === 'object') {
        return normalizeDeep(data);
    }
    if (typeof data === 'string') {
        try {
            return normalizeDeep(JSON.parse(data));
        } catch {
            return data;
        }
    }
    return data;
}

/**
 * 将官网模块保存 payload（或列表里的模块对象）归一化为可对比快照
 * @param {Object} payload
 * @param {number} [conferenceId]
 */
export function pickSiteModuleSnapshot(payload, conferenceId) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const snap = {};
    SITE_MODULE_SCALAR_KEYS.forEach(key => {
        if (payload[key] !== undefined) {
            snap[key] = payload[key];
        }
    });
    if (payload.data !== undefined) {
        snap.data = parseModuleDataField(payload.data);
    }
    if (conferenceId != null) {
        snap.conferenceId = conferenceId;
    }
    return snap;
}

/**
 * 按 after 的字段对齐 before，避免列表项多出的字段造成伪差异
 */
export function alignSiteModuleSnapshots(beforeRaw, afterParams, conferenceId) {
    const after = pickSiteModuleSnapshot(afterParams, conferenceId);
    const beforeFull = beforeRaw ? pickSiteModuleSnapshot(beforeRaw, conferenceId) : {};
    if (!after) {
        return { before: beforeFull, after };
    }
    const before = {};
    Object.keys(after).forEach(key => {
        before[key] = beforeFull?.[key] ?? null;
    });
    return { before, after };
}

function normalizeAgendaDateTime(value) {
    if (value == null || value === '') {
        return value;
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return value;
    }
    return parsed.format('YYYY-MM-DD HH:mm');
}

function normalizeAgendaGuestForSnapshot(guest) {
    if (!guest || typeof guest !== 'object') {
        return null;
    }
    return {
        expertId: guest.expertId ?? null,
        taskId: guest.taskId ?? null,
        discussion: guest.discussion ?? '',
    };
}

function normalizeAgendaGuestList(list) {
    return (list || [])
        .map(normalizeAgendaGuestForSnapshot)
        .filter(Boolean)
        .sort((a, b) => {
            const ea = a.expertId ?? 0;
            const eb = b.expertId ?? 0;
            if (ea !== eb) {
                return ea - eb;
            }
            return (a.taskId ?? 0) - (b.taskId ?? 0);
        });
}

/**
 * 与 saveAgenda / updateAgenda 提交体一致的结构（仅用于操作日志快照，不发起请求）
 */
export function buildAgendaSubmitParams(values, options) {
    const { conferenceId, hallId, agendaId, sortMode, confirm = 1 } = options;
    const params = { ...values, confirm, sortMode: sortMode ?? 0 };
    if (agendaId != null) {
        params.id = agendaId;
    }
    params.hallsId = hallId || 0;

    if (params.time?.length) {
        const dateStr = params.date || dayjs().format('YYYY-MM-DD');
        params.startTime = `${dateStr} ${params.time[0].format('HH:mm')}`;
        params.endTime = `${dateStr} ${params.time[1].format('HH:mm')}`;
        delete params.time;
    }
    delete params.date;
    delete params.hallsName;

    if (params.agendaGuestsList?.length) {
        params.agendaGuestsList = params.agendaGuestsList.map(item => ({
            expertId: item.expertId,
            taskId: item.taskId,
            discussion: item.discussion,
        }));
    }

    if (conferenceId != null) {
        params.conferenceId = conferenceId;
    }

    return params;
}

export function pickAgendaSnapshot(params, agendaId) {
    return {
        id: agendaId ?? params?.id ?? null,
        title: params?.title ?? '',
        startTime: normalizeAgendaDateTime(params?.startTime),
        endTime: normalizeAgendaDateTime(params?.endTime),
        type: params?.type ?? null,
        hidden: params?.hidden ?? null,
        sortMode: params?.sortMode ?? 0,
        agendaGuestsList: normalizeAgendaGuestList(params?.agendaGuestsList),
    };
}

function normalizeBenefitListForLog(benefitList) {
    return (benefitList || [])
        .map(b => ({
            conferenceBenefitId: b.conferenceBenefitId,
            quantity: b.quantity === '' || b.quantity == null ? null : Number(b.quantity),
            boothNumbers: b.boothNumbers ?? '',
            hallsId: b.hallsId ?? 0,
        }))
        .sort((a, b) => {
            if (a.conferenceBenefitId !== b.conferenceBenefitId) {
                return a.conferenceBenefitId - b.conferenceBenefitId;
            }
            return (a.hallsId ?? 0) - (b.hallsId ?? 0);
        });
}

export function summarizeBenefitPayloadForLog(payload) {
    if (!payload) {
        return null;
    }
    const contractAmount = payload.contractAmount;
    return {
        title: payload.title ?? '',
        contractAmount: contractAmount === '' || contractAmount == null ? null : Number(contractAmount),
        benefitList: normalizeBenefitListForLog(payload.benefitList),
    };
}

/**
 * 从编辑抽屉初始快照（与 initialSnapshotRef 结构一致）还原为与提交 payload 同构的日志对象
 */
export function buildOrgBenefitLogPayloadFromEditorSnapshot(snapshotJson, conferenceId, sponsorId) {
    if (!snapshotJson) {
        return null;
    }
    let snap;
    try {
        snap = typeof snapshotJson === 'string' ? JSON.parse(snapshotJson) : snapshotJson;
    } catch (error) {
        console.warn('解析机构权益操作日志快照失败', error);
        return null;
    }

    const orgId = snap.org?.value;
    const benefitList = orgId ? assembleBenefitListPayload(snap.benefits || {}, snap.forums || [], conferenceId, orgId, sponsorId) : [];

    return summarizeBenefitPayloadForLog({
        title: snap.form?.title,
        contractAmount: snap.form?.contractAmount,
        benefitList,
    });
}

/**
 * 权益兑现：仅包含本次变更项的 before/after（与 deliverBenefit 入参字段一致）
 */
export function buildRealizationLogSnapshot(initialSnapshotJson, changedBenefits) {
    let initial = {};
    try {
        initial = initialSnapshotJson ? JSON.parse(initialSnapshotJson) : {};
    } catch (error) {
        console.warn('解析权益兑现初始快照失败', error);
    }

    const before = [];
    const after = [];

    (changedBenefits || []).forEach(item => {
        const stateEntry = Object.values(initial).find(s => s?.benefitId === item.benefitId);
        before.push({
            benefitId: item.benefitId,
            realizationFiles: stateEntry
                ? stringifyRealizationFiles({
                      images: stateEntry.images || [],
                      experts: stateEntry.experts || [],
                  })
                : '',
        });
        after.push({
            benefitId: item.benefitId,
            realizationFiles: item.realizationFiles ?? '',
        });
    });

    return { before, after };
}
